import type { WaveForm } from '../../generated/graphql';

export interface FileNode {
  name: string;
  _id: string;
  path: string;
  type: 'file' | 'folder';
  size?: number;
  contentType?: string;
  uploadedAt?: string;
  children?: FileNode[];
  waveForm?: WaveForm | null;
}

interface FileInput {
  fileName: string;
  _id: string;
  size?: number;
  contentType?: string;
  uploadedAt?: string;
  waveForm?: WaveForm | null;
}

/**
 * Builds a tree structure from a flat list of file paths
 * @param files - Array of files with fileName property
 * @returns Tree structure with folders and files
 */
export function buildFileTree(files: FileInput[]): FileNode[] {
  if (!files.length) return [];

  const tree: FileNode[] = [];
  const folderMap = new Map<string, FileNode>();
  const rootFolders = new Set<string>();

  files.forEach((file) => {
    const parts = file.fileName.split('/');
    let currentPath = '';

    // Create folder nodes
    for (let i = 0; i < parts.length - 1; i++) {
      const folderName = parts[i];
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

      if (i === 0) rootFolders.add(currentPath);

      if (!folderMap.has(currentPath)) {
        const folderNode: FileNode = {
          // Why is folderNode have FileNode type? I have to set id to empty string to get past this for now.
          name: folderName,
          _id: '',
          path: currentPath,
          type: 'folder',
          children: [],
        };

        folderMap.set(currentPath, folderNode);

        if (parentPath && folderMap.has(parentPath)) {
          folderMap.get(parentPath)!.children!.push(folderNode);
        } else if (!parentPath) {
          tree.push(folderNode);
        }
      }
    }

    // Create file node
    const fileName = parts[parts.length - 1];
    const fileNode: FileNode = {
      name: fileName,
      path: file.fileName,
      type: 'file',
      size: file.size,
      contentType: file.contentType,
      uploadedAt: file.uploadedAt,
      waveForm: file.waveForm || null,
      _id: file._id,
    };

    if (parts.length > 1) {
      const parentPath = parts.slice(0, -1).join('/');
      if (folderMap.has(parentPath)) {
        folderMap.get(parentPath)!.children!.push(fileNode);
      }
    } else {
      tree.push(fileNode);
    }
  });

  // Natural sorting that handles numbers correctly
  const sortNodes = (nodes: FileNode[]) => {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;

      // Use natural sort for better number handling
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        caseFirst: 'lower',
      });
    });
    nodes.forEach((node) => {
      if (node.children) sortNodes(node.children);
    });
  };

  sortNodes(tree);
  return tree;
}
